import { useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, User, Shield, Check } from "lucide-react";

const ROLE_LABEL = { super_admin: "Super Admin", admin: "Administrateur", mentor: "Mentor", eleve: "Élève" };

export default function Account() {
  const { user } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm_password) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe");
      return;
    }
    if (f.new_password.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    setLoading(true);
    try {
      await http.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Mot de passe mis à jour — conservez-le précieusement");
      setF({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors du changement");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="account-page">
      <Link to="/app" className="inline-flex items-center gap-2 text-[#5b6577] hover:text-[#00a86b] text-sm font-headings font-semibold mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour au tableau de bord
      </Link>

      <h1 className="font-headings font-black text-3xl md:text-4xl text-[#1a2332] tracking-tight">Mon compte</h1>
      <p className="mt-2 text-[#5b6577]">Gérez vos informations et votre mot de passe.</p>

      <div className="mt-8 bg-white border border-black/5 rounded-2xl shadow-sm p-6 md:p-8" data-testid="account-info">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[#00a86b]/10 text-[#00a86b] flex items-center justify-center shrink-0">
            <User className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="font-headings font-black text-xl text-[#1a2332]">{user?.name}</div>
            <div className="text-sm text-[#5b6577] mt-0.5">{user?.email}</div>
            <div className="mt-2 inline-flex items-center gap-1.5 bg-[#f3f5fa] rounded-full px-2.5 py-1 text-xs font-headings font-bold text-[#1a2332]">
              <Shield className="w-3 h-3" /> {ROLE_LABEL[user?.role] || user?.role}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 bg-white border border-black/5 rounded-2xl shadow-sm p-6 md:p-8" data-testid="password-form">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-[#FF8C42]/10 text-[#FF8C42] flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-headings font-black text-xl text-[#1a2332]">Modifier mon mot de passe</h2>
            <p className="text-sm text-[#5b6577]">Choisissez un mot de passe fort et unique.</p>
          </div>
        </div>

        <div className="space-y-4">
          <Field label="Mot de passe actuel" required>
            <input type="password" autoComplete="current-password" required value={f.current_password}
              onChange={(e) => setF({ ...f, current_password: e.target.value })}
              data-testid="password-current" className="input-account" />
          </Field>
          <Field label="Nouveau mot de passe" required>
            <input type="password" autoComplete="new-password" required minLength={6} value={f.new_password}
              onChange={(e) => setF({ ...f, new_password: e.target.value })}
              data-testid="password-new" className="input-account" placeholder="6 caractères minimum" />
          </Field>
          <Field label="Confirmation du nouveau mot de passe" required>
            <input type="password" autoComplete="new-password" required minLength={6} value={f.confirm_password}
              onChange={(e) => setF({ ...f, confirm_password: e.target.value })}
              data-testid="password-confirm" className="input-account" />
          </Field>
        </div>

        <button type="submit" disabled={loading} data-testid="password-submit"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-[#FF8C42] hover:bg-[#e8763a] disabled:opacity-60 text-white font-headings font-bold rounded-xl px-6 py-3 shadow-sm transition-all">
          {loading ? "Enregistrement…" : (<><Check className="w-4 h-4" /> Enregistrer le nouveau mot de passe</>)}
        </button>

        <style>{`
          .input-account {
            width: 100%;
            background: #f8faff;
            border: 2px solid #e5eaf3;
            color: #1a2332;
            padding: 0.7rem 0.95rem;
            border-radius: 0.8rem;
            font-family: 'Inter', sans-serif;
            font-size: 0.95rem;
            outline: none;
            transition: border-color 0.15s;
          }
          .input-account:focus { border-color: #00a86b; background: #fff; }
        `}</style>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-headings font-semibold text-sm text-[#1a2332]">
        {label} {required && <span className="text-[#FF8C42]">*</span>}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
